import type { ReportProficiencyLevel } from '@/types/reports';

import { getLocalizedText } from '@/utils/localization';

export interface LevelPresentation {
    label: string;
    color: string | undefined;
    icon: string;
    iconUrl: string | undefined;
}

/**
 * Builds a `levelId -> presentation` resolver for the matrix's cells from the report's own
 * `levels`. That list is the single source of truth here: the contract has the response carry the
 * company's whole proficiency scale on every call, so a second lookup against `/proficiencylevel`
 * would only add a request for levels we already hold.
 */
export const createLevelPresentationResolver =
    (levels: ReportProficiencyLevel[]) =>
    (levelId: string | null): LevelPresentation | null => {
        if (!levelId) {
            return null;
        }

        const level = levels.find((candidate) => candidate.id === levelId);

        if (!level) {
            return null;
        }

        return {
            label: getLocalizedText(level.label),
            // The wire sends `null` for "the company left this blank"; the consumers below take
            // `string | undefined`, so both absences collapse to one.
            color: level.iconColor ?? undefined,
            icon: level.icon,
            iconUrl: level.iconUrl ?? undefined,
        };
    };
